import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ClipCard } from '../components/ClipCard';
import { ThemeToggle } from '../components/ThemeToggle';
import { UsageGuide } from '../components/UsageGuide';
import { UploadSection, ActiveFetch } from '../components/UploadSection';
import { toast } from '../hooks/use-toast';
import { api, ClipboardPage as PageType, Upload } from '../utils/api';
import { copyToClipboard, formatClipsForCopy } from '../utils/clipboard';
import { generateQRCode } from '../utils/qrcode';
import { Copy, QrCode, Trash2, Plus, Clock, Unlock, Import, HelpCircle, Radio } from 'lucide-react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';

export function ClipboardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState<PageType | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeData, setQrCodeData] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [showHowTo, setShowHowTo] = useState(false);
  const [live, setLive] = useState(false);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [activeFetches, setActiveFetches] = useState<ActiveFetch[]>([]);

  useEffect(() => {
    loadPage();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const es = new EventSource(`/api/pages/${id}/events`);

    es.addEventListener('ready', () => setLive(true));
    es.onerror = () => setLive(false);

    const parse = (e: MessageEvent) => {
      try {
        return JSON.parse(e.data);
      } catch {
        return {};
      }
    };

    es.addEventListener('clip.created', (e: MessageEvent) => {
      const { clip } = parse(e);
      if (!clip) return;
      setPage((prev) => {
        if (!prev) return prev;
        if (prev.clips.some((c) => c.id === clip.id)) {
          return {
            ...prev,
            clips: prev.clips.map((c) => (c.id === clip.id ? clip : c)),
          };
        }
        return { ...prev, clips: [...prev.clips, clip] };
      });
    });

    es.addEventListener('clip.updated', (e: MessageEvent) => {
      const { clip } = parse(e);
      if (!clip) return;
      setPage((prev) =>
        prev
          ? {
              ...prev,
              clips: prev.clips.map((c) => (c.id === clip.id ? clip : c)),
            }
          : prev
      );
    });

    es.addEventListener('clip.deleted', (e: MessageEvent) => {
      const { clipId } = parse(e);
      if (clipId == null) return;
      setPage((prev) =>
        prev ? { ...prev, clips: prev.clips.filter((c) => c.id !== clipId) } : prev
      );
    });

    es.addEventListener('clips.reordered', (e: MessageEvent) => {
      const { clips } = parse(e);
      if (!Array.isArray(clips)) return;
      setPage((prev) => (prev ? { ...prev, clips } : prev));
    });

    es.addEventListener('clips.cleared', () => {
      setPage((prev) => (prev ? { ...prev, clips: [] } : prev));
    });

    es.addEventListener('page.extended', (e: MessageEvent) => {
      const { expiresAt } = parse(e);
      if (!expiresAt) return;
      setPage((prev) => (prev ? { ...prev, expiresAt } : prev));
    });

    es.addEventListener('page.unlocked', () => {
      setPage((prev) => (prev ? { ...prev, isLocked: false } : prev));
    });

    es.addEventListener('fetch.progress', (e: MessageEvent) => {
      const d = parse(e);
      if (!d.fetchId) return;
      setActiveFetches((prev) => {
        const exists = prev.some(f => f.fetchId === d.fetchId);
        const updated: ActiveFetch = {
          fetchId: d.fetchId,
          filename: d.filename || '',
          bytesReceived: d.bytesReceived ?? 0,
          totalBytes: d.totalBytes ?? 0,
          percent: d.percent ?? null,
          status: d.status,
        };
        return exists
          ? prev.map(f => f.fetchId === d.fetchId ? updated : f)
          : [...prev, updated];
      });
    });

    es.addEventListener('fetch.complete', (e: MessageEvent) => {
      const { fetchId, upload } = parse(e);
      if (!fetchId) return;
      setActiveFetches(prev => prev.filter(f => f.fetchId !== fetchId));
      if (upload) {
        setUploads(prev => [upload, ...prev]);
        toast({ title: 'File fetched', description: upload.filename });
      }
    });

    es.addEventListener('fetch.error', (e: MessageEvent) => {
      const { fetchId, error } = parse(e);
      if (!fetchId) return;
      setActiveFetches(prev =>
        prev.map(f => f.fetchId === fetchId ? { ...f, status: 'error', error } : f)
      );
      // Auto-clear error entries after 5 seconds
      setTimeout(() => {
        setActiveFetches(prev => prev.filter(f => f.fetchId !== fetchId));
      }, 5000);
      toast({ title: 'Fetch failed', description: error, variant: 'destructive' });
    });

    return () => {
      es.close();
      setLive(false);
    };
  }, [id]);

  const loadPage = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const [pageData, uploadData] = await Promise.all([
        api.getPage(id),
        api.getUploads(id).catch(() => []),
      ]);
      setPage(pageData);
      setUploads(uploadData);
    } catch (error: any) {
      if (error.status === 403) {
        setPage({ isLocked: true } as PageType);
      } else if (error.status === 404) {
        toast({
          title: 'Page not found',
          description: 'This clipboard page does not exist or has expired.',
          variant: 'destructive',
        });
        navigate('/');
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load clipboard page.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async () => {
    if (!id) return;
    
    try {
      const pageData = await api.unlockPage(id);
      setPage(pageData);
      toast({
        title: 'Page unlocked',
        description: 'You can now access this clipboard page.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to unlock page.',
        variant: 'destructive',
      });
    }
  };

  const handleExtend = async () => {
    if (!id) return;
    
    try {
      const pageData = await api.extendPage(id);
      setPage(pageData);
      toast({
        title: 'Expiry extended',
        description: 'This page will now expire in 48 hours.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to extend page expiry.',
        variant: 'destructive',
      });
    }
  };

  const handleCreateClip = async () => {
    if (!id || !page || page.clips.length >= 10) return;
    
    try {
      const newClip = await api.createClip(id, `Clip ${page.clips.length + 1}`, '');
      setPage({ ...page, clips: [...page.clips, newClip] });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create new clip.',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateClip = async (clipId: number, title: string, content: string) => {
    if (!id || !page) return;
    
    try {
      const updatedClip = await api.updateClip(id, clipId, title, content);
      setPage({
        ...page,
        clips: page.clips.map(c => c.id === clipId ? updatedClip : c),
      });
      toast({
        title: 'Clip saved',
        description: 'Your changes have been saved.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save clip.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteClip = async (clipId: number) => {
    if (!id || !page) return;
    
    try {
      await api.deleteClip(id, clipId);
      setPage({
        ...page,
        clips: page.clips.filter(c => c.id !== clipId),
      });
      toast({
        title: 'Clip deleted',
        description: 'The clip has been removed.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete clip.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteAll = async () => {
    if (!id || !page) return;
    
    try {
      await api.deleteAllClips(id);
      setPage({ ...page, clips: [] });
      toast({
        title: 'All clips deleted',
        description: 'All clips have been removed.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete clips.',
        variant: 'destructive',
      });
    } finally {
      setShowDeleteAll(false);
    }
  };

  const handleCopyAll = async () => {
    if (!page) return;
    
    const text = formatClipsForCopy(page.clips);
    const success = await copyToClipboard(text);
    
    if (success) {
      toast({
        title: 'Copied to clipboard',
        description: 'All clips have been copied.',
      });
    } else {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard.',
        variant: 'destructive',
      });
    }
  };

  const handleShareQR = async () => {
    const url = window.location.href;
    try {
      const qrData = await generateQRCode(url);
      setQrCodeData(qrData);
      setShowQRCode(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to generate QR code.',
        variant: 'destructive',
      });
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !page || !id) return;
    
    const items = Array.from(page.clips);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Optimistically update the UI
    setPage({ ...page, clips: items });
    
    try {
      // Send the new order to the backend
      const clipOrder = items.map(clip => clip.id);
      const response = await api.reorderClips(id, clipOrder);
      setPage({ ...page, clips: response.clips });
    } catch (error) {
      // Revert on error
      loadPage();
      toast({
        title: 'Error',
        description: 'Failed to reorder clips.',
        variant: 'destructive',
      });
    }
  };

  const handleImport = async () => {
    if (!id || !page) return;
    
    try {
      // Parse the import text using the Copy All format
      const clipRegex = /=== (.+?) ===([\s\S]*?)(?=\n\n===|$)/g;
      const matches: RegExpMatchArray[] = [];
      let match;
      while ((match = clipRegex.exec(importText)) !== null) {
        matches.push(match);
      }
      
      if (matches.length === 0) {
        toast({
          title: 'Invalid format',
          description: 'Please paste text in the "Copy All" format.',
          variant: 'destructive',
        });
        return;
      }
      
      // Check if we have room for all clips
      const availableSlots = 10 - page.clips.length;
      if (matches.length > availableSlots) {
        toast({
          title: 'Too many clips',
          description: `Only ${availableSlots} slots available. ${matches.length} clips in import.`,
          variant: 'destructive',
        });
        return;
      }
      
      // Extract clip data
      const newClips = matches.map(match => {
        const [, rawTitle, content] = match;
        // Extract title without the clip number prefix
        const title = rawTitle.replace(/^clip #\d+\s+/, '');
        return { title: title.trim(), content: content.trim() };
      });
      
      // Create all clips
      for (const clip of newClips) {
        await api.createClip(id, clip.title, clip.content);
      }
      
      // Reload the page to get updated clips
      await loadPage();
      
      toast({
        title: 'Import successful',
        description: `${newClips.length} clips imported.`,
      });
      
      setImportText('');
      setShowImport(false);
    } catch (error) {
      toast({
        title: 'Import failed',
        description: 'An error occurred while importing clips.',
        variant: 'destructive',
      });
    }
  };

  const filteredClips = page?.clips.filter(clip =>
    clip.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    clip.content.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (page?.isLocked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Page Locked</CardTitle>
            <CardDescription>
              This page has been temporarily locked due to too many access attempts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleUnlock} className="w-full">
              <Unlock className="h-4 w-4 mr-2" />
              Unlock Page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!page) {
    return null;
  }

  const expiresIn = new Date(page.expiresAt).getTime() - Date.now();
  const hoursRemaining = Math.floor(expiresIn / (1000 * 60 * 60));

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-4 pb-20">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">Clipboard: {id}</h1>
            <span
              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                live
                  ? 'bg-green-500/10 text-green-700 dark:text-green-400'
                  : 'bg-muted text-muted-foreground'
              }`}
              title={live ? 'Updates stream in real time' : 'Reconnecting…'}
            >
              <Radio className={`h-3 w-3 ${live ? 'animate-pulse' : ''}`} />
              {live ? 'live' : 'offline'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHowTo(true)}
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              How to Use
            </Button>
            <ThemeToggle />
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Clipboard Controls</CardTitle>
            <CardDescription>
              Guest clipboard pages expire in 48 hours and will be purged automatically.
              ({hoursRemaining} hours remaining)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <Button variant="outline" onClick={handleExtend}>
                <Clock className="h-4 w-4 mr-2" />
                Extend 48 Hours
              </Button>
              <Button variant="outline" onClick={handleCopyAll} disabled={page.clips.length === 0}>
                <Copy className="h-4 w-4 mr-2" />
                Copy All
              </Button>
              <Button variant="outline" onClick={() => setShowImport(true)} disabled={page.clips.length >= 10}>
                <Import className="h-4 w-4 mr-2" />
                Import
              </Button>
              <Button variant="outline" onClick={handleShareQR}>
                <QrCode className="h-4 w-4 mr-2" />
                Share QR
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteAll(true)}
                disabled={page.clips.length === 0}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete All
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mb-6 space-y-3">
          <div className="flex gap-3">
            <Input
              placeholder="Search clips..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={handleCreateClip}
              disabled={page.clips.length >= 10}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Clip
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            {page.clips.length} / 10 clips used
          </p>
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="clips">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-4"
              >
                {filteredClips.map((clip, index) => (
                  <ClipCard
                    key={clip.id}
                    clip={clip}
                    pageId={id!}
                    index={index}
                    onUpdate={handleUpdateClip}
                    onDelete={handleDeleteClip}
                  />
                ))}
                {provided.placeholder}
                {filteredClips.length === 0 && (
                  <Card>
                    <CardContent className="text-center py-8">
                      <p className="text-muted-foreground">
                        {searchTerm ? 'No clips match your search.' : 'No clips yet. Create your first clip!'}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <UploadSection
          pageId={id!}
          uploads={uploads}
          activeFetches={activeFetches}
          onUploadsChange={setUploads}
          onFetchStart={(fetchId) => setActiveFetches(prev => [
            ...prev,
            { fetchId, filename: '', bytesReceived: 0, totalBytes: 0, percent: null, status: 'connecting' },
          ])}
        />
      </div>

      <AlertDialog open={showDeleteAll} onOpenChange={setShowDeleteAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all clips?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All clips will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAll} className="bg-destructive text-destructive-foreground">
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showQRCode} onOpenChange={setShowQRCode}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Clipboard</DialogTitle>
            <DialogDescription>
              Scan this QR code to access this clipboard on another device.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4">
            <img src={qrCodeData} alt="QR Code" className="w-64 h-64" />
            <p className="text-sm text-muted-foreground text-center break-all">
              {window.location.href}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showHowTo} onOpenChange={setShowHowTo}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>How to use this clipboard</DialogTitle>
            <DialogDescription>
              Copy-paste the commands below — the clipboard id is already filled in for you.
            </DialogDescription>
          </DialogHeader>
          <UsageGuide pageId={id!} />
        </DialogContent>
      </Dialog>

      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Clips</DialogTitle>
            <DialogDescription>
              Paste clips in the "Copy All" format to import them to this page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="import-text" className="text-sm font-medium">
                Paste your clips here:
              </label>
              <textarea
                id="import-text"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                className="w-full h-64 p-3 border rounded-md font-mono text-sm bg-background"
                placeholder={`=== clip #1 title ===
content here...

=== clip #2 title ===
more content...`}
              />
            </div>
            <div className="text-sm text-muted-foreground">
              <p>Format example:</p>
              <pre className="bg-secondary p-2 rounded-md mt-1">
{`=== clip #1 title ===
content line 1
content line 2

=== clip #2 title ===
more content here`}
              </pre>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => {
                setShowImport(false);
                setImportText('');
              }}>
                Cancel
              </Button>
              <Button onClick={handleImport} disabled={!importText.trim()}>
                <Import className="h-4 w-4 mr-2" />
                Import Clips
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}